import { socketUrl } from 'api/ws';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import fetchChatData, { fetchChatInfo, fetchUnviewedAmount } from 'store/reducers/chatCreator';
import { getDownloadMessage } from 'routes/Documents/Documents';
import fetchDocumentsData, {
  deleteThisDocument,
  deleteThisFolder,
  fetchConvertedFile,
  fetchDocumentMove,
  fetchFolderMove,
  fetchDocumentUpdate,
  fetchFoldersPermissions,
  fetchNewFolder,
  fetchUpdatedRoom,
  updateSigningState,
} from 'store/reducers/documentsSocketsCreator';
import { blockRoom, fetchNotificationUnviewed, fetchRoomNotifications, kickUserCheck } from 'store/reducers/roomsCreator';
import { setChatMessage, setChatStatus, setIsLoadedSession, updateChatLastMessage } from 'store/slices/chatsSlice';
import { SocketIO } from 'utils/WorkerSocket';
import { getCookie, setCookie } from 'helpers/cookies';
import { setIsOpenFailedPayment, setIsOpenSuccessfulPayment } from 'store/slices/windowStateSlice';
import { useNavigate, useSearchParams } from 'react-router';
import { fetchGlobalUsers, fetchUsersOnSokcet } from 'store/reducers/usersGroupCreator';
import { updateRoomsBySockets } from 'store/reducers/userDataCreator';
import { updateAnalysisStatus, updateTranscription } from 'store/reducers/aiFunctionsCreator';
import { refresTokensHandle } from 'helpers/tokens';

export default function SessionComponent() {
  const [isPausedWS, setIsPausedWS] = React.useState(false);
  const socketRef = React.useRef<SocketIO | null>(null);

  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userEmail = searchParams.get('email') || '';
  
  const { userData } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { chatStatus, chatMessage, selectedChat, isLoadedSession } = useAppSelector((store) => store.chat);

  const locale = i18n.language || localStorage.getItem('language') || userData?.language || 'en';

  const chatCases = {
    pending: (message: any) => {},
    send: (message: any) => {
      socketRef.current?.send('chat', message);
      dispatch(setChatStatus('pending'));
      dispatch(setChatMessage({ id: '', status: 'pending', message: '', replied_to: null, attachment_ids: null }));
    },
  };

  const updateToken = async (oldToken: string) => {
    console.log(`need refresh token`);
    const currentToken = getCookie('athena-token');
    if (oldToken !== currentToken) {
      setIsPausedWS(false);
    } else {
      const refresh_token = getCookie('refresh_token');
      await refresTokensHandle(refresh_token);      
      setIsPausedWS(false);
    }
  }

  React.useEffect(() => {    
    if (!isPausedWS) {
      const token = getCookie('athena-token');
      socketRef.current = new SocketIO();
      socketRef.current.connect({ url: socketUrl, token, email: userEmail});
      socketRef.current.on('connect', () => {
        setCookie('sid', socketRef.current?.socket?.id || '', 1);
        console.log(`The session is open at: ${new Date().toLocaleString()}`);
      });

      socketRef.current.on('connect_to_server', async (data: any) => {
        console.log('connect_to_server:',data);
        const parsedData = JSON.parse(data);
        if (parsedData.success === false) {
          socketRef.current?.disconnect();
          setTimeout(() => {
            updateToken(token)
          }, 1000);
        }
      });

      socketRef.current.on('auth:get', () => {
        dispatch(setIsLoadedSession(true));
        console.log('Session is authed');
      });

      socketRef.current.on('new_chat', (message: any) => {
        console.log('new chat created -', message);
        const data = JSON.parse(message);
        dispatch(fetchChatInfo({ chatId: data.new_chat_id, ownerId: userData?.id! }));
      });

      socketRef.current.on('new_global_chat', (message: any) => {
        const data = JSON.parse(message);
        console.log('new global chat created -', data);
        // The 'data' object does not contain the 'new_chat_id' field.
        // dispatch(fetchChatInfo({ chatId: data.new_chat_id, ownerId: userData?.id! }));
      });

      socketRef.current.on('document_create', async (message: any) => {
        const data = JSON.parse(message);
        dispatch(fetchDocumentsData({ type: 'replace', websocketData: data, navigate }));
        console.log(`Get session message: ${message}`);
      });

      socketRef.current.on('document_ready', async (message: any) => {
        const data = JSON.parse(message);
        dispatch(fetchConvertedFile({data, translation: { t, i18n }, getDownloadMessage}));
        console.log(`Get session message: ${message}`);
      });

      socketRef.current.on('signing_update', async (message: any) => {
        const data = JSON.parse(message);
        dispatch(fetchDocumentsData({ type: 'replace', websocketData: data, navigate}));
        dispatch(updateSigningState(data));
        console.log(`Get session message: ${message}`);
      });

      socketRef.current.on('folder_create', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Get session message: ${message}`);
        dispatch(fetchNewFolder(data));
      });

      socketRef.current.on('document_delete', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Document deleted: ${message}`);
        dispatch(deleteThisDocument(data));
      });

      socketRef.current.on('document_move', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Document moved: ${message}`);
        dispatch(fetchDocumentMove(data));
      });

      socketRef.current.on('folder_move', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Folder moved: ${message}`);
        dispatch(fetchFolderMove(data));
      });

      socketRef.current.on('folder_delete', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Folder deleted: ${message}`);
        dispatch(deleteThisFolder(data));
      });

      socketRef.current.on('document_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Document updated: ${message}`);
        dispatch(fetchDocumentUpdate(data));
      });

      socketRef.current.on('document_replace', async (message: any) => {
        console.log(`Document version updated: ${message}`);
        dispatch(fetchDocumentUpdate({ ...message, prev_version: message.prev_version }));
      });

      socketRef.current.on('permissions_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Document updated: ${message}`);
        dispatch(fetchFoldersPermissions(data));
      });

      socketRef.current.on('notification_create', async (message: any) => {
        console.log(`Notification created: ${message}`);
        dispatch(fetchRoomNotifications({}));
        dispatch(fetchNotificationUnviewed({}))
      });

      socketRef.current.on('rooms_update', async (message: any) => {
        console.log(`Message: ${message}`);
        dispatch(updateRoomsBySockets());
      });

      socketRef.current.on('successful_payment', async (message: any) => {
        console.log('Payment info:', message);
        dispatch(setIsOpenSuccessfulPayment(true));
      });

      socketRef.current.on('failed_payment', async (message: any) => {
        console.log('Payment info:', message);
        dispatch(setIsOpenFailedPayment(true));
      });

      socketRef.current.on('chat', async (message: any) => {
        console.log(`Get session message in chat: ${message}`);
        const data = JSON.parse(message);
        dispatch(fetchUnviewedAmount({ data, count: 1 }));
        dispatch(fetchChatData({ data, type: 'replace' }));
        dispatch(
          updateChatLastMessage({
            chat_id: data.chat_id,
            content: data.message,
            id: data.id,
            status: 'sent',
            author: {
              id: data.id,
              name: data.user_name,
              email: data.email,
            },
            created_at: data.created_at,
            attachment_ids: data.attachment_ids,
          })
        );
      });

      socketRef.current.on('message_from_ai_assistant', async (message: any) => {
        console.log('Get session message from AI:', message);
        const data = JSON.parse(message);
        dispatch(fetchChatData({ data, type: 'replace' }));
      });

      socketRef.current.on('agreement_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Agreement updated: ${message}`);
        dispatch(fetchUpdatedRoom({socketData: data, navigate, t}));
      });

      socketRef.current.on('members_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Agreement updated: ${message}`);
        dispatch(fetchUsersOnSokcet(data));
        dispatch(fetchGlobalUsers())
      });

      socketRef.current.on('kick_user', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Message: ${message}`);
        dispatch(kickUserCheck({idRoom: data.room_id, navigate}));
      });
      
      socketRef.current.on('transcription_status_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Transcription update status: ${message}`);
        dispatch(updateTranscription({...data, t}));
      });

      socketRef.current.on('report_status_update', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Report update status: ${message}`);
        dispatch(updateAnalysisStatus({...data, t}));
      });

      socketRef.current.on('update_user_blocked', async (message: any) => {
        const data = JSON.parse(message);
        console.log(`Message: ${message}`);
        dispatch(blockRoom({data, navigate, t}));
      });


      socketRef.current.on('error', (error: any) => console.log(error, 'Session error'));

      socketRef.current.on('disconnect', () => {
        setCookie('sid', '', 1);
        setIsPausedWS(true);
        console.log(`The session is closed at: ${new Date().toLocaleString()}`);
      });
    }

    return () => {
      if (socketRef.current?.socket?.connected) socketRef.current?.disconnect();
    };
  }, [isPausedWS, locale]);

  React.useEffect(() => {
    if (dataRoom?.id && socketRef.current?.socket?.connected && isLoadedSession) {
      const messageNewRoom = { room_id: dataRoom?.id };
      console.log('Send session message', messageNewRoom);
      socketRef?.current!.send('init', messageNewRoom);
    }
  }, [dataRoom?.id, socketRef.current?.socket?.connected, isLoadedSession]);

  React.useMemo(
    () =>
      document.addEventListener('click', function () {
        if (socketRef.current?.socket?.disconnected) {
          setIsPausedWS(false);
        }
      }),
    [socketRef.current?.socket?.disconnected, isPausedWS]
  );

  React.useEffect(() => {
    const data = {
      room_id: dataRoom?.id,
      message: chatMessage.message,
      chat_id: selectedChat?.id,
      created_at: chatMessage.id,
      replied_to: chatMessage.replied_to ? chatMessage.replied_to.id : null,
    };

    chatCases[chatStatus](data);
  }, [chatStatus]);

  // Функционал прерывания сессии при неактивной вкладке браузера (сворачивание браузера/переход на другую вкладку)
  // React.useMemo(() => document.addEventListener('visibilitychange', function(){
  //   if (document.hidden && !isPausedWS && sessionChanel.current?.readyState === 1){
  //     setIsPausedWS(true);
  //     // console.log(`Сlosed: ${new Date().toLocaleString()}`);
  //     // sessionChanel.current?.send(`Сlosed: ${new Date().toLocaleString()}`);
  //     sessionChanel.current?.close();
  //   } else if (!document.hidden && isPausedWS) {
  //     setIsPausedWS(false);
  //   }
  // }), [sessionChanel.current?.readyState]);

  // Ручное прерыване канала вебсокета для тестирования
  // const closeWS = () => {
  //   sessionChanel.current?.close();
  //   // console.log(`Сlosed: ${new Date().toLocaleString()}`);
  //   // setIsPausedWS(true);
  // }
  // return (
  //   !isPausedWS
  //     ? <button style={{position: 'absolute'}} onClick={closeWS}>{ 'Остановить соединение' }</button>
  //     : null
  // );

  return null;
}
